import { ReactNode, useEffect, useMemo, useState } from "react";
import type { MenuProps } from "antd";
import { Button, Modal, Select, message } from "antd";
import { DownloadOutlined, FolderOutlined } from "@ant-design/icons";
import ADAIcon from "../../../icons/ADAIcon";
import AssetApi from "../../../../api/AssetApi";
import { AssetDto } from "../../../../generated/dashboard-client";
import { env } from "../../../../env/env";
import "./assetActionMenu.scss";

export const ASSET_CATEGORY_OPTIONS = [
  { key: "LOGOS", label: "Logos", singular: "Logo" },
  { key: "FONTS", label: "Fonts", singular: "Font" },
  // { key: "ICONS", label: "Icons", singular: "Icon" },
  { key: "GRAPHICS", label: "Graphics", singular: "Graphic" },
];

export function uiCategoryFor(category?: string): string {
  return category === "PHOTOS" ? "GRAPHICS" : category || "";
}

export function categorySingularFor(category?: string): string {
  const ui = uiCategoryFor(category);
  return ASSET_CATEGORY_OPTIONS.find((c) => c.key === ui)?.singular || ui;
}

export function formatAssetUploadedDate(dateStr?: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
}

export function useAssetImageDims(assets: AssetDto[]) {
  const [dims, setDims] = useState<Record<string, { w: number; h: number }>>({});
  useEffect(() => {
    assets.forEach((a) => {
      if (!a.id || dims[a.id]) return;
      if (!a.fileType?.startsWith("image/")) return;
      const img = new Image();
      img.onload = () =>
        setDims((prev) => ({
          ...prev,
          [a.id!]: { w: img.naturalWidth, h: img.naturalHeight },
        }));
      img.src = `${env.s3Path}/${a.path}`;
    });
  }, [assets, dims]);
  return dims;
}

interface BuildMenuOpts {
  asset: AssetDto;
  dims?: { w: number; h: number };
  onAda: (asset: AssetDto) => void;
  onDownload: (asset: AssetDto) => void;
  onMove: (asset: AssetDto) => void;
  onRemove: (asset: AssetDto) => void;
  removeLabel: string;
  removeIcon: ReactNode;
}

export function buildAssetMenuItems(opts: BuildMenuOpts): MenuProps["items"] {
  const { asset, dims, onAda, onDownload, onMove, onRemove, removeLabel, removeIcon } = opts;
  const singular = categorySingularFor(asset.category);
  const ext = (asset.name || "").split(".").pop()?.toUpperCase() || "";
  const supportsAda = asset.category !== "FONTS";
  const titleParts: string[] = [];
  if (singular) titleParts.push(singular);
  if (ext) titleParts.push(ext);
  if (dims) titleParts.push(`${dims.w} × ${dims.h}px`);

  return [
    {
      key: "info",
      label: (
        <div className="asset-dropdown-info">
          <div className="asset-dropdown-info-title">{titleParts.join(" · ")}</div>
          <div className="asset-dropdown-info-date">
            Uploaded {formatAssetUploadedDate(asset.createdDate)}
          </div>
        </div>
      ),
      disabled: true,
    },
    { type: "divider" as const, key: "divider" },
    ...(supportsAda
      ? [
          {
            key: "ada",
            icon: (
              <span className="ada-icon-wrap">
                <ADAIcon color="#333" />
              </span>
            ),
            label: "ADA Text",
            onClick: () => onAda(asset),
          },
        ]
      : []),
    {
      key: "download",
      icon: <DownloadOutlined className="asset-menu-icon" />,
      label: "Download",
      onClick: () => onDownload(asset),
    },
    {
      key: "move",
      icon: <FolderOutlined className="asset-menu-icon" />,
      label: "Move to Category",
      onClick: () => onMove(asset),
    },
    {
      key: "remove",
      icon: (
        <span className="asset-menu-icon asset-menu-remove-icon">{removeIcon}</span>
      ),
      label: removeLabel,
      onClick: () => onRemove(asset),
    },
  ];
}

interface MoveCategoryModalProps {
  open: boolean;
  assetIds: string[];
  excludeCategory?: string;
  onCancel: () => void;
  onMoved: (updated: AssetDto[], category: string) => void;
}

export function MoveCategoryModal({
  open,
  assetIds,
  excludeCategory,
  onCancel,
  onMoved,
}: MoveCategoryModalProps) {
  const [choice, setChoice] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setChoice("");
  }, [open]);

  const options = useMemo(() => {
    return ASSET_CATEGORY_OPTIONS.filter((c) => c.key !== excludeCategory);
  }, [excludeCategory]);

  function close() {
    if (submitting) return;
    onCancel();
  }

  function confirm() {
    if (!choice || assetIds.length === 0) return;
    setSubmitting(true);
    AssetApi.bulkMoveCategory(assetIds, choice)
      .then((resp) => {
        const updated = resp.data || [];
        onMoved(updated, choice);
        const label = ASSET_CATEGORY_OPTIONS.find((c) => c.key === choice)?.label || choice;
        message.success(
          assetIds.length === 1
            ? `Moved to ${label}`
            : `Moved ${assetIds.length} assets to ${label}`
        );
      })
      .catch(() => message.error("Failed to move assets"))
      .finally(() => setSubmitting(false));
  }

  return (
    <Modal
      className={"clean one move-category-modal"}
      open={open}
      onCancel={close}
      footer={null}
      destroyOnClose
    >
      <header>
        <h2>Move assets to category</h2>
      </header>

      <div className="move-category-body">
        <Select
          size="large"
          placeholder="Select a category"
          value={choice || undefined}
          onChange={(v) => setChoice(v)}
          options={options.map((c) => ({ value: c.key, label: c.label }))}
          style={{ width: "100%" }}
        />
      </div>

      <footer>
        <Button size="large" onClick={close} disabled={submitting}>
          Cancel
        </Button>
        <Button
          size="large"
          className="primary"
          onClick={confirm}
          loading={submitting}
          disabled={!choice}
        >
          Move
        </Button>
      </footer>
    </Modal>
  );
}
